import { Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, Plus, Rocket } from 'lucide-react';
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  Outlet,
  createFileRoute,
  useNavigate,
  useRouter,
  useRouterState,
} from '@tanstack/react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { AppShell, type AppShellTab } from '@/components/shared/AppShell';
import { ForbiddenPage } from '@/components/shared/ForbiddenPage';
import {
  MentionLinksContext,
  type MentionLinks,
} from '@/components/shared/rich-text/mention-links';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ApiError } from '@/lib/api-error';
import { meOptions } from '@/features/auth/api';
import { useIsProjectAdmin, usePermission } from '@/features/auth/use-permission';
import { projectsListOptions, projectTagsOptions } from '@/features/projects/api';
import { CreateEpicDialog } from '@/features/epics/components/CreateEpicDialog';
import { TaskFormDialog } from '@/features/tasks/components/TaskFormDialog';
import { TaskFormProvider, useTaskForm } from '@/features/tasks/task-form-context';
import { tasksListOptions } from '@/features/tasks/api';
import { notesListOptions } from '@/features/notes/api';
import { useDeleteTask } from '@/features/tasks/hooks';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ProjectActionsMenu } from '@/features/projects/components/ProjectActionsMenu';
import { ProjectSwitcher } from '@/features/projects/components/ProjectSwitcher';
import { DeploymentDrawer } from '@/features/timeline/components/DeploymentDrawer';

// Shareable-task-URL param. Lives at the layout level so every child tab
// (timeline, tasks, workload, …) inherits it — pasting `?task=<id>` on any tab
// reopens that task's dialog. `.catch(undefined)` keeps a hand-edited/bogus
// value from breaking validation.
const projectSearchSchema = z.object({
  task: z.string().uuid().optional().catch(undefined),
});
export type ProjectSearch = z.infer<typeof projectSearchSchema>;

export const Route = createFileRoute('/_authed/projects/$projectId')({
  validateSearch: projectSearchSchema,
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(meOptions()),
      context.queryClient.ensureQueryData(projectsListOptions()),
    ]),
  component: ProjectLayout,
  errorComponent: ProjectErrorComponent,
});

// A 403 from any project-scoped loader (timeline, members, tags, …) means the
// user opened a project they aren't a member of — show the dedicated warning
// page with a way back. Anything else bubbles to the root error boundary.
function ProjectErrorComponent({ error }: { error: Error }) {
  if (error instanceof ApiError && error.status === 403) {
    return <ForbiddenPage />;
  }
  throw error;
}

function ProjectLayout() {
  return (
    <TaskFormProvider>
      <MentionLinksProvider>
        <ProjectShell />
      </MentionLinksProvider>
    </TaskFormProvider>
  );
}

// Router-backed navigation for `#`-mention chips in every rich-text surface of
// the project (task description, comments, notes). Ctrl/⌘-click opens the
// mentioned task / note page in a new tab; a plain click on a note chip closes
// any open task dialog and navigates to that note. Sits inside TaskFormProvider
// so it can close the dialog.
function MentionLinksProvider({ children }: { children: ReactNode }) {
  const { projectId } = Route.useParams();
  const router = useRouter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { close } = useTaskForm();

  const links = useMemo<MentionLinks>(
    () => ({
      href: (kind, id) => {
        const location =
          kind === 'note'
            ? router.buildLocation({
                to: '/projects/$projectId/notes',
                params: { projectId },
                search: { note: id },
              })
            : router.buildLocation({
                to: '/projects/$projectId/tasks/$taskId',
                params: { projectId, taskId: id },
              });
        return router.history.createHref(location.href);
      },
      openNote: (noteId) => {
        void queryClient.ensureQueryData(notesListOptions(projectId)).then((notes) => {
          if (!notes.some((n) => n.id === noteId)) {
            toast('That note no longer exists.');
            return;
          }
          close();
          void navigate({
            to: '/projects/$projectId/notes',
            params: { projectId },
            search: { note: noteId },
          });
        });
      },
    }),
    [router, navigate, queryClient, close, projectId],
  );

  return <MentionLinksContext.Provider value={links}>{children}</MentionLinksContext.Provider>;
}

// Two-way sync between the `?task=<id>` URL param and the shared TaskFormDialog
// state. Router coupling is confined here (the provider stays router-agnostic so
// it can be rendered without a router in tests). Writing/reading the browser URL
// is a legitimate useEffect case — the "sync to an external system" exception,
// not data fetching, which still goes through TanStack Query.
function useTaskFormUrlSync(projectId: string) {
  const navigate = useNavigate();
  const { task: taskParam } = Route.useSearch();
  const { state } = useTaskForm();
  // Shared cache entry — the dialog already fetches this list, so resolving an
  // id → TaskResponse costs nothing extra beyond the tabs that lack it.
  const { data: tasks } = useQuery(tasksListOptions(projectId, {}));

  // Tracks the last URL param Effect B acted on, so both effects can tell a
  // fresh (unhandled) deep-link param apart from one that merely reflects the
  // open modal or lingers for a render during close.
  const lastHandledParam = useRef<string | undefined>(undefined);

  // Effect A — state → URL. Every openEdit (row, gantt bar, subtask) reflects
  // into the address bar; close clears it. Exception: a fresh deep-link param
  // (state closed, not yet handled by Effect B) is on its way to the full page —
  // leave it alone so this effect doesn't race Effect B's redirect and strand
  // the user on a param-less tab with no task shown.
  useEffect(() => {
    const desired = state.kind === 'edit' ? state.task.id : undefined;
    if (desired === taskParam) return;
    if (desired === undefined && taskParam && taskParam !== lastHandledParam.current) return;
    void navigate({ to: '.', search: (prev) => ({ ...prev, task: desired }), replace: true });
  }, [state, taskParam, navigate]);

  // Effect B — shared/pasted `?task=<id>` link (deep link / paste / reload)
  // redirects to the full task page rather than opening the centered modal.
  // Reacts only to a genuine change of the URL param, tracked via the ref above:
  // without it, closing the modal re-runs this effect (state flips to `closed`
  // while the param is still set for one render, before Effect A's clear lands)
  // and it would fire again. `replace` avoids a Back-button loop back onto the
  // redirecting URL. The not-found toast is guarded on the list having resolved
  // so it can't fire mid-load.
  useEffect(() => {
    if (tasks === undefined) return;
    if (taskParam === lastHandledParam.current) return;
    lastHandledParam.current = taskParam;
    if (!taskParam || state.kind !== 'closed') return;
    const found = tasks.find((t) => t.id === taskParam);
    if (found) {
      void navigate({
        to: '/projects/$projectId/tasks/$taskId',
        params: { projectId, taskId: taskParam },
        replace: true,
      });
    } else {
      toast('That task no longer exists.');
      void navigate({ to: '.', search: (prev) => ({ ...prev, task: undefined }), replace: true });
    }
  }, [taskParam, state.kind, tasks, projectId, navigate]);
}

function ProjectShell() {
  const { projectId } = Route.useParams();
  const navigate = useNavigate();
  const { data: me } = useSuspenseQuery(meOptions());
  const { data: projects } = useSuspenseQuery(projectsListOptions());
  const { data: tags } = useQuery(projectTagsOptions(projectId));

  const project = useMemo(() => projects.find((p) => p.id === projectId), [projects, projectId]);
  const activeTab = useActiveTab();
  const canCreateTask = usePermission(projectId, 'CreateTask');
  const canCreateEpic = usePermission(projectId, 'CreateEpic');
  const canScheduleDeployment = usePermission(projectId, 'ApproveDeployment');
  const canManageProject = usePermission(projectId, 'ManageMembers');
  const isAdmin = useIsProjectAdmin(projectId);
  const { state, openCreate, close } = useTaskForm();
  const [deploymentOpen, setDeploymentOpen] = useState(false);
  const [createEpicOpen, setCreateEpicOpen] = useState(false);
  const [deleteTaskId, setDeleteTaskId] = useState<string | null>(null);
  const deleteMutation = useDeleteTask(() => {
    setDeleteTaskId(null);
    close();
  });
  useTaskFormUrlSync(projectId);

  if (!project) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg p-8 text-text">
        <div className="max-w-md space-y-2 text-center">
          <h1 className="text-2xl font-medium">Project not found</h1>
          <p className="text-sm text-text-muted">
            This project either doesn&apos;t exist or you don&apos;t have access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <AppShell
        projectCrumb={
          <ProjectSwitcher
            currentUserId={me.id}
            project={project}
            onSwitch={(nextId) =>
              // Land on the same view in the other project. Search params are
              // dropped — filters reference the old project's tags/members.
              // Reports is admin-only, so it falls back to the timeline.
              navigate({
                to: `/projects/$projectId/${activeTab === 'report' ? 'timeline' : activeTab}`,
                params: { projectId: nextId },
              })
            }
          />
        }
        currentUser={{ id: me.id, displayName: me.display_name, email: me.email }}
        activeTab={activeTab}
        onTabChange={(tab) =>
          navigate({
            to: `/projects/$projectId/${tab}`,
            params: { projectId },
          })
        }
        visibleTabs={isAdmin ? undefined : ['timeline', 'tasks', 'workload', 'leaves', 'notes']}
        actions={
          <>
            {canCreateTask || canCreateEpic ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="primary" size="sm">
                    <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                    Add
                    <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canCreateEpic && (
                    <DropdownMenuItem onClick={() => setCreateEpicOpen(true)}>
                      Epic
                    </DropdownMenuItem>
                  )}
                  {canCreateTask && (
                    <DropdownMenuItem onClick={() => openCreate()}>Task</DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            {canScheduleDeployment ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setDeploymentOpen(true)}
                className="bg-deploy-scheduled-bg text-deploy-scheduled-fg hover:bg-deploy-scheduled-bg/70"
              >
                <Rocket className="h-3.5 w-3.5" aria-hidden="true" />
                Schedule a Milestone
              </Button>
            ) : null}
            <ProjectActionsMenu project={project} canManageProject={canManageProject} />
          </>
        }
      >
        <Outlet />
      </AppShell>

      <Suspense fallback={null}>
        <TaskFormDialog
          projectId={projectId}
          open={state.kind !== 'closed'}
          mode={state.kind === 'edit' ? 'edit' : 'create'}
          task={state.kind === 'edit' ? state.task : undefined}
          defaultEpicId={state.kind === 'create' ? state.defaultEpicId : undefined}
          parentTask={state.kind === 'create' ? state.parentTask : undefined}
          fromTasks={state.kind === 'edit' ? state.fromTasks : undefined}
          onClose={close}
          onDelete={
            state.kind === 'edit'
              ? () => {
                  setDeleteTaskId(state.task.id);
                  close();
                }
              : undefined
          }
        />
      </Suspense>

      <Dialog open={deleteTaskId !== null} onOpenChange={(open) => !open && setDeleteTaskId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete task?</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTaskId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteTaskId && deleteMutation.mutate(deleteTaskId)}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateEpicDialog
        projectId={projectId}
        tags={tags ?? []}
        open={createEpicOpen}
        onClose={() => setCreateEpicOpen(false)}
      />

      {canScheduleDeployment ? (
        <Suspense fallback={null}>
          <DeploymentDrawer
            projectId={projectId}
            deployment={deploymentOpen ? undefined : null}
            memberDisplayNames={{}}
            onClose={() => setDeploymentOpen(false)}
          />
        </Suspense>
      ) : null}
    </>
  );
}

function useActiveTab(): AppShellTab {
  const path = useRouterState({ select: (s) => s.location.pathname });
  if (path.endsWith('/tasks')) return 'tasks';
  if (path.endsWith('/workload')) return 'workload';
  if (path.endsWith('/leaves')) return 'leaves';
  if (path.endsWith('/report')) return 'report';
  if (path.endsWith('/notes')) return 'notes';
  return 'timeline';
}
